/**
 * SignInPage — Entra ID OAuth entry point.
 *
 * Layout matches the sibling ojb-rfp-agent login: full-bleed OJB
 * landscape photo as a dim background, a single centered white card
 * holding everything — logo, product name, subtitle, description
 * panel, Microsoft sign-in button, and a restriction footer. No
 * tri-color bar; no text sitting loose on the photo.
 *
 * Background image: public/login-bg.jpg. Falls back to pine if the
 * image fails to load.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/** Reads `?error=...` from the current URL and maps it to a
 *  pilot-readable explanation. The auth callback redirects users
 *  here with these error codes when defense-in-depth checks
 *  reject their sign-in:
 *    - wrong_tenant: account belongs to a different Entra tenant
 *    - domain_not_allowed: email isn't an @ojb.com address
 *  Anything else (or no param) falls back to null and no banner. */
function readSignInError():
  | { title: string; detail: string; code: string }
  | null {
  if (typeof window === 'undefined') return null;
  const code = new URLSearchParams(window.location.search).get('error');
  if (!code) return null;
  // Security audit H4: server now emits a single `signin_blocked`
  // code for any auth-callback rejection (tenant mismatch, domain
  // not @ojb.com, etc.) instead of distinct codes per cause. This
  // prevents an unauthenticated probe from differentiating
  // "wrong tenant" (account exists, just wrong tenant) from
  // "domain_not_allowed" (your domain isn't us) — both leak info
  // useful for targeted phishing.
  //
  // The legacy codes (wrong_tenant, domain_not_allowed) are still
  // mapped here to the same banner so any still-cached redirect
  // links don't break, but the API stopped emitting them.
  if (
    code === 'signin_blocked' ||
    code === 'wrong_tenant' ||
    code === 'domain_not_allowed'
  ) {
    return {
      code,
      title: 'Sign-in blocked',
      detail:
        'Your Microsoft account is not authorized to use this application. Please sign in with your OJB @ojb.com account.',
    };
  }
  if (code === 'session_expired') {
    return {
      code,
      title: 'Session expired',
      detail:
        'Your session ended for inactivity. Please sign in again to pick up where you left off.',
    };
  }
  // OJB Studio Hub bridge-flow specific codes. The bridge receiver
  // (apps/api/src/routes/bridge.ts) redirects here when JWT
  // verification fails or the receiver hasn't been configured
  // yet. Surfacing the distinct code makes it easy for a user
  // who hits this from the hub tile to tell support which path
  // failed without screenshotting the URL bar (which is hidden
  // inside the Teams iframe anyway).
  if (code === 'bridge_invalid') {
    return {
      code,
      title: 'Bridge sign-in failed',
      detail:
        'The Studio Hub sign-in token could not be verified. Please use the Microsoft sign-in button below, or contact your admin if the issue persists.',
    };
  }
  if (code === 'bridge_disabled') {
    return {
      code,
      title: 'Bridge sign-in not configured',
      detail:
        'Studio Hub sign-in is not yet enabled on this app. Please use the Microsoft sign-in button below, or contact your admin to finish bridge setup.',
    };
  }
  return {
    code,
    title: 'Sign-in failed',
    detail: 'Something went wrong with sign-in. Please try again.',
  };
}

export function SignInPage() {
  const returnTo = window.location.origin;
  const signInUrl = `${API_BASE}/auth/signin?returnTo=${encodeURIComponent(returnTo)}`;
  const signInError = readSignInError();

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 py-12">
      {/* Background layers — photo + soft dark overlay so the white
          card pops. Overlay is gentler than a full darkening so the
          photo still reads around the card edges. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(43, 42, 47, 0.25) 0%, rgba(43, 42, 47, 0.45) 100%), url('/login-bg.jpg')",
          backgroundColor: '#375542', // pine fallback if image fails
        }}
      />

      {/* Card — mockup .signin-card: bone bg (warm-neutral, NOT
          stark white), no border, 24px radius, charcoal 30% drop
          shadow, asymmetric padding 40/36/32 so the logo-to-top
          breathing room is wider than the button-to-bottom gap. */}
      <div
        className="relative z-10 w-full max-w-[460px] rounded-[24px] bg-bone px-9 pb-8 pt-10 text-center shadow-[0_24px_60px_rgba(43,42,47,0.30)]"
      >
        {/* Logo — real OJB mark, charcoal transparent PNG. 48px tall
            per mockup .signin-card__logo. */}
        <img
          src="/ojb-logo-charcoal.png"
          alt="OJB"
          className="mx-auto mb-5 block h-12 w-auto"
        />

        {/* Product name — 22px Aeonik 600 charcoal, mb-1.5 to the
            subtitle. */}
        <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-charcoal">
          OJB Meeting Minutes
        </h1>

        {/* Subtitle — mockup .signin-card__subtitle: 13px /65, mb-5
            to whatever follows (error banner OR description). */}
        <p className="mb-5 text-[13px] text-charcoal/65">
          Sign in with your OJB Microsoft account to continue
        </p>

        {/* Sign-in error banner — only renders when the auth
            callback redirected here with an `?error=` code (e.g.
            wrong tenant or non-@ojb.com email). Mockup
            .signin-card__error: terra-cotta 10% bg + 30% border,
            12px radius, 12/16 padding, left-aligned. Title 12px
            700 terra-cotta; detail 12px /75. */}
        {signInError && (
          <div
            role="alert"
            className="mb-5 rounded-xl border border-terra-cotta/30 bg-terra-cotta/10 px-4 py-3 text-left"
          >
            <div className="text-[12px] font-bold text-terra-cotta">
              {signInError.title}
            </div>
            <div className="mt-0.5 text-[12px] text-charcoal/75">
              {signInError.detail}
            </div>
            {/* Small monospace error-code line. Lets a user pasting
                a screenshot to support state which path failed
                without us having to ask "what was in the URL bar?"
                — useful inside the Teams iframe where the URL is
                hidden. */}
            <div className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-charcoal/50">
              code: {signInError.code}
            </div>
          </div>
        )}

        {/* Description panel — mockup .signin-card__desc: cloud 50%
            bg, NO border, 16px radius, 14/18 padding, 13px /80
            line-1.55, left-aligned. Holds the product blurb so the
            card reads as more than just a login button. */}
        <div className="mb-6 rounded-2xl bg-cloud/50 px-[18px] py-3.5 text-left text-[13px] leading-[1.55] text-charcoal/80">
          Paste a Teams transcript, a Granola summary, or your own shorthand.
          OJB Meeting Minutes structures it into a polished, on-brand meeting
          document - ready to edit, export, or email in seconds.
        </div>

        {/* Microsoft sign-in button — mockup .signin-btn: charcoal
            bg + cream text, 12px radius, 13/18 padding, 14px 500.
            Hover: bg ink + -translate-y-px + 8/20 charcoal shadow. */}
        <a
          href={signInUrl}
          className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-charcoal px-[18px] py-[13px] text-[14px] font-medium text-cream no-underline transition-all duration-200 hover:-translate-y-px hover:bg-ink hover:shadow-[0_8px_20px_rgba(43,42,47,0.30)]"
        >
          <MicrosoftIcon />
          Sign in with Microsoft
        </a>

        {/* Restriction footer — mockup .signin-card__footer: 11px
            /55 line-1.55, mt-[18px]. The @ojb.com pill is bold
            pine to make the gating crystal clear. */}
        <p className="mt-[18px] text-[11px] leading-[1.55] text-charcoal/55">
          Restricted to OJB employees with an{' '}
          <b className="font-bold text-pine">@ojb.com</b> Microsoft account.
          Your account is used only to verify your identity.
        </p>
      </div>

      {/* Locations line below the card — mockup .signin-cities:
          cream text + 0.04em tracking + soft black text-shadow so
          it reads cleanly on the photo. */}
      <p className="relative z-10 mt-8 text-center text-[11px] tracking-[0.04em] text-cream drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)]">
        OJB Landscape Architecture · Boston · Dallas · Denver · Houston · Philadelphia · San Diego
      </p>
    </div>
  );
}

function MicrosoftIcon() {
  // Mockup .ms-logo: 8px squares with 1px gap rendered via inline-grid
  // for crisp pixel rendering on HiDPI screens. Mockup colors are
  // slightly brighter than Microsoft's published Brand Central swatch
  // — kept as-is to match the approved mockup.
  return (
    <span
      aria-hidden
      className="inline-grid"
      style={{
        gridTemplateColumns: '8px 8px',
        gridTemplateRows: '8px 8px',
        gap: '1px',
      }}
    >
      <span style={{ background: '#F35325' }} />
      <span style={{ background: '#81BC06' }} />
      <span style={{ background: '#05A6F0' }} />
      <span style={{ background: '#FFBA08' }} />
    </span>
  );
}
